import Cookies from 'js-cookie';

import { getBooleanConfig, getStringListConfig } from '@zg-rentals/frontend-config/client';
import { CometUserRoleType, FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import { getCookieAsBoolean } from '@zg-rentals/zillow-rental-manager/client';
import { canAccessModularListing } from '@zg-rentals/zillow-rental-manager/utils';

import {
  ZG_COMP_INSIGHTS,
  ZRM_AUTO_SPOTLIGHT_UI_ALLOWLIST_EMAILS,
  ZRM_AUTO_SPOTLIGHT_UI_ENABLED,
  ZRM_FEATURE_TOUR_ENABLED,
  ZRM_FEED_OVERRIDES_ENABLED,
  ZRM_MULTIPLEX_LISTING_FLOW_ENABLED,
  ZRM_QR_PHOTO_UPLOAD_MULTIPLEX_ENABLED,
  ZRM_SHOW_CANCELLED_TOURS,
  ZRM_SMART_ASSIGN_UI_ALLOWLIST_EMAILS,
  ZRM_SMART_ASSIGN_UI_ENABLED,
  ZRM_TOTAL_MONTHLY_PRICE_ENABLED,
  ZRM_USER_MANAGEMENT_ENABLED,
  ZRM_USER_ONBOARDING_ENABLED,
} from '../constants/FeatureFlagConstants';
import { USER_ONBOARDING_DISMISSED_KEY } from '../constants/UserOnboardingConstants';

import propertyUtils from './propertyUtils';

import type {
  GetUserPropertyDetailsListingTypeWebDto,
  GetUserPropertyDetailsResponseWebDto,
  GetUserPropertySpotlightOverviewResponseDto,
  RentalManagerAuthCheckUserInfoWebApiDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const canAccessTopOfFunnelPremium = ({
  isFreemiumEnabled,
  isTopOfFunnelEnrollmentEligible,
  isActiveProductTypePremium,
}: {
  isFreemiumEnabled: boolean;
  isTopOfFunnelEnrollmentEligible: boolean;
  isActiveProductTypePremium: boolean;
}): boolean => {
  return isFreemiumEnabled && (isActiveProductTypePremium || isTopOfFunnelEnrollmentEligible);
};

const canAccessMLReactivationFlow = ({
  lastActivatedMs,
  isFeed,
  isMultifamily,
}: {
  lastActivatedMs?: number;
  isFeed?: boolean;
  isMultifamily?: boolean;
}) => {
  return Boolean((lastActivatedMs || isFeed) && !isMultifamily);
};

const canAccessSFCostsAndFees = ({ isFeed, isMultifamily }: { isFeed: boolean; isMultifamily: boolean }) =>
  !isFeed && !isMultifamily;

const canAccessFastPost = ({
  isMultifamily,
  lastActivatedMs,
  isFeed,
  restrictions,
}: {
  isMultifamily: boolean;
  lastActivatedMs: number | undefined;
  isFeed: boolean;
  restrictions: { applications: { isAccessRestricted: boolean } };
}) => {
  return !isMultifamily && !lastActivatedMs && !isFeed && !restrictions?.applications?.isAccessRestricted;
};

const canAccessPostingPathTourScheduling = ({
  isFeed,
  isInReactivationFlow,
  canAccessRelisting,
  active,
}: {
  isFeed: boolean;
  isInReactivationFlow: boolean;
  canAccessRelisting: boolean;
  active: boolean;
}) => {
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessRelistingFlow = ({ canBeRelisted }: { canBeRelisted: boolean }) => {
  return canBeRelisted;
};

const canAccessListingPreview = ({
  active,
  isFeed,
  isMultifamily,
  listingType,
}: Partial<{
  active: boolean;
  isFeed: boolean;
  isMultifamily: boolean;
  listingType: GetUserPropertyDetailsListingTypeWebDto;
}>) => {
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: { listingType } });

  return !active && !isRoomForRent && !isFeed && !isMultifamily;
};

const canAccessTotalMonthlyPriceWithFee = ({ isEligible = false }: { isEligible?: boolean }) =>
  getCookieAsBoolean(ZRM_TOTAL_MONTHLY_PRICE_ENABLED) || isEligible;

const isTourSchedulingEnabledForProperty = ({
  active,
  canBeRelisted,
  isFeed,
  isInReactivationFlow,
}: {
  active: boolean;
  canBeRelisted: boolean;
  isFeed: boolean;
  isInReactivationFlow: boolean;
}) => {
  const canAccessRelisting = canAccessRelistingFlow({ canBeRelisted });
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessUserOnboardingExistingUser = ({ user }: { user: RentalManagerAuthCheckUserInfoWebApiDto | null }) => {
  if (!user) return false;

  if (!user?.restrictions?.isAllowed) {
    return false;
  }

  if (typeof window !== 'undefined') {
    try {
      if (sessionStorage.getItem(USER_ONBOARDING_DISMISSED_KEY) === 'true') {
        return false;
      }
    } catch {
      // Silently fail if sessionStorage is unavailable (e.g., restricted browser context)
    }
  }

  const hasListings = (user?.summary?.totalListingCount ?? 0) > 0;

  if (getCookieAsBoolean(ZRM_USER_ONBOARDING_ENABLED)) {
    return hasListings;
  }

  return Boolean(
    hasListings &&
    (!user.firstName ||
      !user.lastName ||
      !user.profiles?.profileType ||
      !user.profiles?.propertyCountRange ||
      !user?.firstName?.trim() ||
      !user?.lastName?.trim()),
  );
};

const canAccessUserOnboardingNewUser = ({
  user,
}: {
  user: {
    restrictions?: { isAllowed: boolean };
    features: { canViewOnboardingFlow: boolean };
  } | null;
}) => {
  if (user?.restrictions?.isAllowed && user.features.canViewOnboardingFlow) {
    return true;
  }

  return false;
};

const canAccessMultiplexListingFlow = ({
  canAccessMultiplexListingFlowResponse,
}: {
  canAccessMultiplexListingFlowResponse: boolean;
}) => {
  const areRequiredConditionsSatisfied = canAccessMultiplexListingFlowResponse === true;
  const areOverrideConditionsSatisfied = getCookieAsBoolean(ZRM_MULTIPLEX_LISTING_FLOW_ENABLED);

  return areRequiredConditionsSatisfied || areOverrideConditionsSatisfied;
};

const shouldShowBrokerFeeAlert = ({ state }: { state: string }) => {
  return state ? state === 'MA' : false;
};

const canFeedConnectSwitchSource = ({ user }: { user: { features: { canSwitchListingSource: boolean } } | null }) =>
  user?.features?.canSwitchListingSource;

const canAccessAddFeedListingsModal = ({ user }: { user: { features: { canViewClaimFeedListing: boolean } } | null }) =>
  user?.features?.canViewClaimFeedListing;

const canAccessFeatureNotifications = ({
  user,
}: {
  user: { features: { canAccessFeatureLaunchNotifications: boolean } } | null;
}) => user?.features?.canAccessFeatureLaunchNotifications;

const isNonAdvertiser = ({ user }: { user: { roleTypes: Array<CometUserRoleType> } | null }) =>
  Boolean(user?.roleTypes?.includes('NON_ADVERTISER_CUSTOMER' as CometUserRoleType));

const canAccessClaimPath = ({
  cookie,
  hasAnyPropertiesToClaim,
  isScreenMobile,
  isNonAdvertiser: isNonAdvertiserValue,
}: {
  cookie?: string;
  hasAnyPropertiesToClaim: boolean;
  isScreenMobile: boolean;
  isNonAdvertiser: boolean;
}) =>
  !isScreenMobile && (hasAnyPropertiesToClaim || isNonAdvertiserValue) && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessClaimMorePath = ({
  isScreenMobile,
  hasAnyClaimedListings,
  hasNewPropertiesToClaim,
  cookie,
}: {
  isScreenMobile: boolean;
  hasAnyClaimedListings: boolean;
  hasNewPropertiesToClaim: boolean;
  cookie: string;
}) =>
  !isScreenMobile && hasAnyClaimedListings && hasNewPropertiesToClaim && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessSpotlightListingsFirstUseModal = ({
  isScreenMobile,
  isSpotlightListingsEligible,
  cookie,
}: {
  isScreenMobile: boolean;
  isSpotlightListingsEligible: boolean;
  appConfig?: unknown;
  cookieEnabledServices?: unknown;
  cookie?: string;
}) => Boolean(!isScreenMobile && isSpotlightListingsEligible && (!cookie || Cookies.get(cookie) !== 'true'));

const canAccessListingSpotlightFilter = ({
  user,
  spotlightOverview,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null;
  // Also show for LS customers (SPOTLIGHT_CUSTOMER role) — the same signal the single-family list routing uses.
}) =>
  Boolean(
    (user?.isSingleFamilyEnterpriseCustomer && spotlightOverview?.isEligible) ||
    user?.roleTypes?.includes(CometUserRoleType.SPOTLIGHT_CUSTOMER),
  );

const isAutoSpotlightAllowlistedEmail = (email?: string | null): boolean => {
  if (!email) {
    return false;
  }
  const normalizedEmail = email.toLowerCase();
  const allowlist = [
    ...ZRM_AUTO_SPOTLIGHT_UI_ALLOWLIST_EMAILS,
    ...getStringListConfig('zrmAutoSpotlightUiAllowlistEmails', []),
  ];
  return allowlist.some((allowed) => allowed.trim().toLowerCase() === normalizedEmail);
};

// Only customers with an active Listing Spotlight contract can set the optimization mode. hasSpotlightedListings
// reflects the contract; pinned/auto/available are all 0 for fully-used contracts predating the engine.
const hasActiveSpotlightContract = (spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null): boolean =>
  Boolean(spotlightOverview?.isEligible) && Boolean(spotlightOverview?.hasSpotlightedListings);

const canAccessAutoSpotlightUi = ({
  user,
  spotlightOverview,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null;
}): boolean =>
  (getCookieAsBoolean(ZRM_AUTO_SPOTLIGHT_UI_ENABLED) || isAutoSpotlightAllowlistedEmail(user?.email)) &&
  hasActiveSpotlightContract(spotlightOverview);

const isSmartAssignAllowlistedEmail = (email?: string | null): boolean => {
  if (!email) {
    return false;
  }
  const normalizedEmail = email.toLowerCase();
  const allowlist = [
    ...ZRM_SMART_ASSIGN_UI_ALLOWLIST_EMAILS,
    ...getStringListConfig('zrmSmartAssignUiAllowlistEmails', []),
  ];
  return allowlist.some((allowed) => allowed.trim().toLowerCase() === normalizedEmail);
};

// Separate from the panel gate: pilot customers already see the panel, so before the global rollout the Smart
// Assign controls need their own cookie. Allowlisted emails get in without the cookie.
const canAccessSmartAssignUi = ({ user }: { user: RentalManagerAuthCheckUserInfoWebApiDto | null }): boolean =>
  getBooleanConfig('zrmSmartAssignUiEnabled', false) ||
  getCookieAsBoolean(ZRM_SMART_ASSIGN_UI_ENABLED) ||
  isSmartAssignAllowlistedEmail(user?.email);

const canAccessSmartAssignPanel = ({
  user,
  spotlightOverview,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null;
}): boolean => canAccessSmartAssignUi({ user }) && hasActiveSpotlightContract(spotlightOverview);

const canAccessPortfolioSection = ({ user }: { user: RentalManagerAuthCheckUserInfoWebApiDto | null }): boolean =>
  Boolean(user) &&
  !user?.isEnterpriseCustomer &&
  !user?.userManagementCompanyId &&
  (getBooleanConfig('canAccessUserManagement', false) || getCookieAsBoolean(ZRM_USER_MANAGEMENT_ENABLED));

const canAccessFeatureTour = (): boolean => {
  return getCookieAsBoolean(ZRM_FEATURE_TOUR_ENABLED);
};

const canAccessCondensedFlow = ({
  canAccessRelistingFlow,
  canAccessMLReactivationFlow,
  lastActivatedMs,
}: {
  canAccessRelistingFlow?: boolean;
  canAccessMLReactivationFlow?: boolean;
  lastActivatedMs?: number;
}): boolean => !canAccessRelistingFlow && !canAccessMLReactivationFlow && !lastActivatedMs;

const canAccessFeedOverrides = ({
  user,
  section,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  section?: FeedOverrideSection;
}): boolean =>
  getBooleanConfig('canAccessFeedOverrides', false) ||
  getCookieAsBoolean(ZRM_FEED_OVERRIDES_ENABLED) ||
  (section !== FeedOverrideSection.SPECIAL_OFFERS && Boolean(user?.impersonatorEmail || user?.impersonatorUserToken));

const canAccessMediaCredits = (): boolean => true;

const canAccessAIListingCreationTestB = ({
  canAccessRelistingFlow,
  canAccessMLReactivationFlow,
  lastActivatedMs,
}: {
  canAccessRelistingFlow?: boolean;
  canAccessMLReactivationFlow?: boolean;
  lastActivatedMs?: number;
}): boolean => !canAccessRelistingFlow && !canAccessMLReactivationFlow && !lastActivatedMs;

const canAccessCompetitiveInsights = (): boolean => getCookieAsBoolean(ZG_COMP_INSIGHTS);

/** Whether the tour calendar renders cancelled tours; must match the tour-calendar page's own gate. */
const canShowCancelledTours = (): boolean =>
  getBooleanConfig('canShowCancelledTours', false) || getCookieAsBoolean(ZRM_SHOW_CANCELLED_TOURS);

/** Phone photo upload on the multiplex step, where photos go to every unit in the building session. */
const canAccessQrPhotoUploadMultiplex = (): boolean =>
  typeof window !== 'undefined' &&
  (getBooleanConfig('canAccessQrPhotoUploadMultiplex', false) ||
    getCookieAsBoolean(ZRM_QR_PHOTO_UPLOAD_MULTIPLEX_ENABLED));

/** Self-serve onboarding blocks photo changes. Unknown details read as unrestricted; see useIsPhotoUploadRestricted. */
const isPhotoUploadRestricted = (listingDetails: GetUserPropertyDetailsResponseWebDto | undefined) =>
  Boolean(
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress &&
    listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted,
  );

export default {
  canAccessModularListing,
  canAccessTopOfFunnelPremium,
  canAccessMLReactivationFlow,
  canAccessSFCostsAndFees,
  canAccessFastPost,
  canAccessPostingPathTourScheduling,
  canAccessTotalMonthlyPriceWithFee,
  canAccessMultiplexListingFlow,
  canAccessRelistingFlow,
  canAccessListingPreview,
  canAccessUserOnboardingNewUser,
  canAccessUserOnboardingExistingUser,
  isTourSchedulingEnabledForProperty,
  shouldShowBrokerFeeAlert,
  canFeedConnectSwitchSource,
  canAccessAddFeedListingsModal,
  canAccessFeatureNotifications,
  isNonAdvertiser,
  canAccessClaimPath,
  canAccessClaimMorePath,
  canAccessSpotlightListingsFirstUseModal,
  canAccessListingSpotlightFilter,
  canAccessAutoSpotlightUi,
  canAccessSmartAssignPanel,
  canAccessSmartAssignUi,
  canAccessCompetitiveInsights,
  canShowCancelledTours,
  canAccessPortfolioSection,
  canAccessFeatureTour,
  canAccessFeedOverrides,
  canAccessMediaCredits,
  canAccessCondensedFlow,
  canAccessAIListingCreationTestB,
  canAccessQrPhotoUploadMultiplex,
  isPhotoUploadRestricted,
};
